/* eslint-disable max-len */
import {
    useCallback, MutableRefObject, useState,
    useEffect, useMemo,
} from 'react'
import {
    GridApiPro,
    GridColumnDimensions,
} from '@mui/x-data-grid-pro'
import isEqual from 'lodash/isEqual'
import useSkyDataGridLocalStorage from '../useSkyDataGridLocalStorage'
import {
    SkyDataGridColumnsState,
    SkyDataGridLocalStorage,
} from '../../SkyBaseDataGrid.types'
import {
    initFields,
    defaultSetColumnsStateConverter,
    defaultInitColumnsStateConverter,
} from './utils'

const usePersistedColumnsState = ({
    persistentKey,
    apiRef,
    dataSourceKey,
    setStateConverter = defaultSetColumnsStateConverter,
    initStateConverter = defaultInitColumnsStateConverter,
}:{
    persistentKey: string,
    apiRef: MutableRefObject<GridApiPro>,
    dataSourceKey?: string,
    setStateConverter?: (value: SkyDataGridColumnsState, oldState?: SkyDataGridLocalStorage) => SkyDataGridLocalStorage,
    initStateConverter?: (dataGridState: SkyDataGridLocalStorage) => SkyDataGridColumnsState,
}) => {
    const localStorageInitState = useMemo(() => {
        return setStateConverter(initFields)
    }, [setStateConverter])

    const {
        dataGridState,
        setDataGridState,
        getStorageValue,
    } = useSkyDataGridLocalStorage<SkyDataGridLocalStorage>(persistentKey,
        localStorageInitState)

    const initialState = useMemo(() => {
        return initStateConverter(dataGridState)
    }, [
        dataGridState,
        initStateConverter,
    ])

    const setInitialState = useCallback((value:{
        orderedFields?: string[],
        dimensions?: Record<string, GridColumnDimensions>,
    }) => {
        const currentDataGridState = getStorageValue()
        const newGridState = setStateConverter(value, currentDataGridState)

        setDataGridState(newGridState)
    }, [
        setStateConverter,
        setDataGridState,
        getStorageValue,
    ])

    /* We need this to track when we change tabs and restore state,
     because DataGrid re-renders but does not re-mount nor re-initialize state */
    const [
        localKeys,
        setLocalKeys,
    ] = useState(() => {
        return {
            dataSourceKey,
            persistentKey,
        }
    })

    const saveState = useCallback(() => {
        const currentState = apiRef.current.exportState()
        const orderedColumns = currentState?.columns?.orderedFields
        const dimensions = currentState?.columns?.dimensions

        setInitialState({
            orderedFields: orderedColumns,
            dimensions,
        })
    }, [
        apiRef,
        setInitialState,
    ])

    const onRestoreState = useCallback(() => {
        const currentState = apiRef.current.exportState()

        if (!isEqual(initialState?.orderedFields, currentState.columns.orderedFields)
            || !isEqual(initialState?.dimensions, currentState.columns.dimensions)) {
            apiRef.current.restoreState({
                columns: {
                    orderedFields: [...(initialState?.orderedFields || [])],
                    dimensions: {
                        ...(initialState?.dimensions || {}),
                    },
                },
            })
        }
    }, [
        apiRef,
        initialState,
    ])

    useEffect(() => {
        if (dataSourceKey !== localKeys.dataSourceKey
            || persistentKey !== localKeys.persistentKey) {
            setLocalKeys({
                dataSourceKey,
                persistentKey,
            })
            onRestoreState()
        }
    }, [
        dataSourceKey,
        localKeys.dataSourceKey,
        localKeys.persistentKey,
        persistentKey,
        apiRef,
        onRestoreState,
    ])

    return {
        initialState,
        onColumnStateChange: saveState,
    }
}

export default usePersistedColumnsState
